// @ts-nocheck
import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import Icon, { ICONS } from '../../ui/icons';
import { useLocalization, type Language } from '../../../config/localization';
import CollectionSelectModal from '../../ui/overlays/modals/CollectionSelectModal';

interface Collection {
  id: number;
  name: string;
  description: string | null;
  is_default: boolean;
  is_private: boolean;
  can_edit: boolean;
  total_images?: number;
}

interface ImageCollection {
  id: number;
  name: string;
  is_default: boolean;
}

interface CollectionActionsMenuProps {
  imageId: number;
  imageCollections: string; // JSON array string
  collections: Collection[];
  onAddToCollections: (imageId: number, collectionIds: number[]) => Promise<void>;
  onRemoveFromCollection: (imageId: number, collectionId: number) => Promise<void>;
  onCopyPrompt?: () => void;
  onMoveToTrash?: () => void;
  onDownload?: () => void;
  onDownloadWebP?: () => void;
  onRecreate?: () => void;
  language: Language;
  showButton?: boolean; // Whether to show the trigger button (default true)
  isDownloadSuccess?: boolean; // Show checkmark feedback after download
  isWebPDownloadSuccess?: boolean; // Show checkmark feedback after WebP download
}

export interface CollectionActionsMenuRef {
  openAtPosition: (x: number, y: number) => void;
  close: () => void;
}

const CollectionActionsMenu = forwardRef<CollectionActionsMenuRef, CollectionActionsMenuProps>(({
  imageId,
  imageCollections,
  collections,
  onAddToCollections,
  onRemoveFromCollection,
  onCopyPrompt,
  onMoveToTrash,
  onDownload,
  onDownloadWebP,
  onRecreate,
  language,
  showButton = true,
  isDownloadSuccess = false,
  isWebPDownloadSuccess = false
}, ref) => {
  const t = useLocalization(language);
  const [isOpen, setIsOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [openDirection, setOpenDirection] = useState<'up' | 'down'>('up');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Parse image collections from JSON string
  const parsedCollections: ImageCollection[] = React.useMemo(() => {
    try {
      return JSON.parse(imageCollections || '[]');
    } catch {
      return [];
    }
  }, [imageCollections]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openAtPosition: (x: number, y: number) => {
      const menuHeight = 250;
      const menuWidth = 224;

      // Adjust position to stay within viewport
      const adjustedX = Math.min(x, window.innerWidth - menuWidth - 8);
      const adjustedY = y;

      // Determine if menu should open up or down
      const spaceBelow = window.innerHeight - y;
      const shouldOpenUp = spaceBelow < menuHeight && y > menuHeight;

      setMenuPosition({ x: Math.max(8, adjustedX), y: adjustedY });
      setOpenDirection(shouldOpenUp ? 'up' : 'down');
      setIsOpen(true);
    },
    close: () => setIsOpen(false)
  }));

  // Calculate menu position when opening via button
  const openFromButton = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuHeight = 250;
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;

      const shouldOpenUp = spaceAbove > menuHeight || spaceAbove > spaceBelow;

      setMenuPosition({
        x: rect.right - 224,
        y: shouldOpenUp ? rect.top - 4 : rect.bottom + 4
      });
      setOpenDirection(shouldOpenUp ? 'up' : 'down');
      setIsOpen(true);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        (!buttonRef.current || !buttonRef.current.contains(event.target as Node))
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) setIsOpen(false);
    };

    if (isOpen) {
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpen) {
      setIsOpen(false);
    } else {
      openFromButton();
    }
  };

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    setShowAddModal(true);
  };

  const handleCopyPromptClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onCopyPrompt?.();
  };

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onDownload?.();
  };

  const handleDownloadWebPClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onDownloadWebP?.();
  };

  const handleRecreateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onRecreate?.();
  };

  const handleTrashClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onMoveToTrash?.();
  };

  const handleUpdateCollections = async (selectedIds: number[], initialIds: number[]) => {
    setIsLoading(true);
    try {
      // Calculate which collections to add (in selected but not in initial)
      const toAdd = selectedIds.filter(id => !initialIds.includes(id));
      // Calculate which collections to remove (in initial but not in selected)
      const toRemove = initialIds.filter(id => !selectedIds.includes(id));

      // Add to new collections
      if (toAdd.length > 0) {
        await onAddToCollections(imageId, toAdd);
      }

      // Remove from unchecked collections
      if (toRemove.length > 0) {
        for (const collectionId of toRemove) {
          await onRemoveFromCollection(imageId, collectionId);
        }
      }

      setShowAddModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  const dropdownMenu = isOpen && (
    <div
      ref={menuRef}
      className="fixed w-56 bg-gray-900 border border-gray-700 rounded-lg shadow-xl overflow-hidden"
      style={{
        top: openDirection === 'down' ? menuPosition.y : 'auto',
        bottom: openDirection === 'up' ? `calc(100vh - ${menuPosition.y}px)` : 'auto',
        left: Math.max(8, menuPosition.x),
        zIndex: 9999,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Copy prompt option */}
      {onCopyPrompt && (
        <button
          onClick={handleCopyPromptClick}
          className="w-full px-3 py-2 flex items-center gap-2 text-left text-sm text-gray-200 hover:bg-gray-800 transition-colors"
        >
          <Icon name={ICONS.COPY} size={14} className="text-blue-400" />
          <span>{t.gallery?.copyPrompt || 'Копировать промпт'}</span>
        </button>
      )}

      {/* Download option */}
      {onDownload && (
        <button
          onClick={handleDownloadClick}
          className={`w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-colors ${
            isDownloadSuccess
              ? 'text-green-300 bg-green-900/30'
              : 'text-gray-200 hover:bg-gray-800'
          }`}
        >
          <Icon name={isDownloadSuccess ? ICONS.CHECK : ICONS.DOWNLOAD} size={14} className="text-green-400" />
          <span>{isDownloadSuccess ? (t.gallery?.downloaded || 'Downloaded!') : (t.gallery?.download || 'Download')}</span>
        </button>
      )}

      {/* Download WebP option */}
      {onDownloadWebP && (
        <button
          onClick={handleDownloadWebPClick}
          className={`w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-colors ${
            isWebPDownloadSuccess
              ? 'text-green-300 bg-green-900/30'
              : 'text-gray-200 hover:bg-gray-800'
          }`}
        >
          <Icon name={isWebPDownloadSuccess ? ICONS.CHECK : ICONS.DOWNLOAD} size={14} className="text-green-400" />
          <span>{isWebPDownloadSuccess ? (t.gallery?.downloadedWebP || 'Downloaded WebP!') : (t.gallery?.downloadWebP || 'Download WebP')}</span>
        </button>
      )}

      {/* Recreate option */}
      {onRecreate && (
        <button
          onClick={handleRecreateClick}
          className="w-full px-3 py-2 flex items-center gap-2 text-left text-sm text-gray-200 hover:bg-gray-800 transition-colors"
        >
          <Icon name={ICONS.REFRESH} size={14} className="text-orange-400" />
          <span>{t.gallery?.recreate || 'Recreate'}</span>
        </button>
      )}

      {/* Add to collection option */}
      <button
        onClick={handleAddClick}
        className="w-full px-3 py-2 flex items-center gap-2 text-left text-sm text-gray-200 hover:bg-gray-800 transition-colors"
      >
        <Icon name={ICONS.FOLDER_PLUS} size={14} className="text-purple-400" />
        <span>{t.gallery?.addToCollectionMenu || 'Добавить в коллекцию...'}</span>
      </button>

      {/* Move to trash option */}
      {onMoveToTrash && (
        <>
          <div className="border-t border-gray-700 my-1" />
          <button
            onClick={handleTrashClick}
            className="w-full px-3 py-2 flex items-center gap-2 text-left text-sm text-gray-200 hover:bg-red-900/30 hover:text-red-300 transition-colors"
          >
            <Icon name={ICONS.TRASH} size={14} className="text-red-400" />
            <span>{t.gallery?.moveToTrash || 'В корзину'}</span>
          </button>
        </>
      )}
    </div>
  );

  return (
    <>
      {/* Menu trigger button - only show if showButton is true */}
      {showButton && (
        <button
          ref={buttonRef}
          onClick={handleToggle}
          disabled={isLoading}
          className="p-1.5 bg-gray-600/80 hover:bg-gray-600 rounded-lg backdrop-blur-sm shadow-lg transition-colors disabled:opacity-50"
          title={t.gallery?.collectionActions || 'Действия'}
        >
          <Icon name={ICONS.MORE} size={14} className="text-white" />
        </button>
      )}

      {/* Dropdown menu rendered via portal to escape overflow containers */}
      {createPortal(dropdownMenu, document.body)}

      {/* Add to collection modal */}
      <CollectionSelectModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleUpdateCollections}
        collections={collections.filter(c => c.can_edit)}
        selectedCollectionIds={parsedCollections.map(c => c.id)}
        selectedLanguage={language}
      />
    </>
  );
});

CollectionActionsMenu.displayName = 'CollectionActionsMenu';

export default CollectionActionsMenu;
